// Client-side view of access state, so a tap updates the UI before the server answers
// (spec §8: optimistic unlock, the lock must never blink). The server stays the authority:
// every /play response overwrites these numbers.
import type { Lang, Me, PlayResponse, StoriesResponse, StoryListItem } from '@fonus/shared';
import type { QueryClient } from '@tanstack/react-query';
import { keys } from '../api/queries.ts';

type Access = Pick<StoriesResponse, 'freeLeft' | 'premiumUntil'>;

export function isLocked(item: StoryListItem, access: Access): boolean {
  return !item.isDraft && !access.premiumUntil && !item.opened && access.freeLeft === 0;
}

function recompute(data: StoriesResponse): StoriesResponse {
  return { ...data, items: data.items.map((i) => ({ ...i, locked: isLocked(i, data) })) };
}

/** Would playing this item spend a free slot? */
export function spendsFreeSlot(item: StoryListItem, access: Access): boolean {
  return !item.isDraft && !access.premiumUntil && !item.opened && access.freeLeft > 0;
}

function updateAllLangs(qc: QueryClient, fn: (d: StoriesResponse) => StoriesResponse): void {
  for (const lang of ['uz', 'ru', 'en'] as Lang[]) {
    const data = qc.getQueryData<StoriesResponse>(keys.stories(lang));
    if (data) qc.setQueryData(keys.stories(lang), fn(data));
  }
}

/** Optimistic: mark the story opened and spend one free slot (in every language's cache). */
export function optimisticOpen(qc: QueryClient, storyId: string): void {
  updateAllLangs(qc, (d) => {
    const item = d.items.find((i) => i.id === storyId);
    if (!item || !spendsFreeSlot(item, d)) return d;
    return recompute({
      ...d,
      freeLeft: d.freeLeft - 1,
      items: d.items.map((i) => (i.id === storyId ? { ...i, opened: true } : i)),
    });
  });
}

/** Authoritative numbers from /play (or /prefetch). */
export function applyPlayResponse(qc: QueryClient, res: PlayResponse, storyId: string): void {
  updateAllLangs(qc, (d) =>
    recompute({
      ...d,
      freeLeft: res.freeLeft,
      freeLimit: res.freeLimit,
      premiumUntil: res.premiumUntil,
      premiumEndedAt: res.premiumEndedAt,
      items: d.items.map((i) =>
        i.id === storyId && res.unlockedVia === 'FREE' ? { ...i, opened: true } : i,
      ),
    }),
  );
  const me = qc.getQueryData<Me>(keys.me);
  if (me) {
    qc.setQueryData<Me>(keys.me, {
      ...me,
      freeLeft: res.freeLeft,
      premiumUntil: res.premiumUntil,
      premiumEndedAt: res.premiumEndedAt,
    });
  }
}

/** "Continue listening": the most recently played unfinished story. */
export function continueItem(items: StoryListItem[]): StoryListItem | undefined {
  return items
    .filter((i) => i.progressSec > 0 && !i.listened && !i.locked)
    .sort(
      (a, b) => Date.parse(b.progressUpdatedAt ?? '0') - Date.parse(a.progressUpdatedAt ?? '0'),
    )[0];
}
